import { lotusHealthZod } from '@lotushealth/lotushealth_js_sdk';
import type {
  MetriportCommunicationDestinationT,
  ReferralDestinationT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { formatPhoneNumber } from '@/shared/lib/formatters';

/** Directory a referral recipient was found in, which is also how it is sent. */
export type ReferralChannel = 'nppes' | 'metriport';

export const REFERRAL_CHANNEL_LABELS: Readonly<Record<ReferralChannel, string>> = {
  nppes: 'NPPES',
  metriport: 'Metriport',
};

export type NppesRecipient = {
  channel: 'nppes';
  destination: ReferralDestinationT;
};

export type MetriportRecipient = {
  channel: 'metriport';
  destination: MetriportCommunicationDestinationT;
};

export type ReferralRecipient = NppesRecipient | MetriportRecipient;

/** Read a recipient stored on a draft; anything malformed reads as no recipient. */
export function parseReferralRecipient(value: unknown): ReferralRecipient | null {
  if (
    value === null ||
    typeof value !== 'object' ||
    !('channel' in value) ||
    !('destination' in value)
  ) {
    return null;
  }

  if (value.channel === 'nppes') {
    const parsed = lotusHealthZod.searchReferralDestinationsResponse.safeParse({
      type: 'success',
      destinations: [value.destination],
      next_offset: null,
    });
    if (!parsed.success || parsed.data.type !== 'success') {
      return null;
    }
    const [destination] = parsed.data.destinations;
    if (!destination) {
      return null;
    }
    const { channel, ...normalizedDestination } = destination;
    void channel;
    return { channel: 'nppes', destination: normalizedDestination };
  }

  if (value.channel === 'metriport') {
    const parsed = lotusHealthZod.searchMetriportDestinationsResponse.safeParse({
      type: 'success',
      destinations: [value.destination],
    });
    if (!parsed.success || parsed.data.type !== 'success') {
      return null;
    }
    const [destination] = parsed.data.destinations;
    return destination ? { channel: 'metriport', destination } : null;
  }

  return null;
}

export function recipientName(recipient: ReferralRecipient): string {
  if (recipient.channel === 'nppes') {
    return recipient.destination.provider_name;
  }
  return recipient.destination.name?.trim() || `NPI ${recipient.destination.npi}`;
}

export function recipientOrganization(recipient: ReferralRecipient): string {
  return recipient.channel === 'nppes' ? (recipient.destination.organization_name ?? '') : '';
}

/** One line locating the recipient, e.g. "300 Pasteur Dr, Palo Alto, CA". */
export function recipientLocation(recipient: ReferralRecipient): string {
  const parts =
    recipient.channel === 'nppes'
      ? [
          recipient.destination.practice_location.address_line_1,
          recipient.destination.practice_location.city,
          recipient.destination.practice_location.state,
        ]
      : [
          recipient.destination.addressLine,
          recipient.destination.city,
          recipient.destination.state,
        ];
  return parts.filter((part): part is string => Boolean(part?.trim())).join(', ');
}

/** How the referral reaches the recipient, e.g. "Fax (650) 555-0100". */
export function recipientDeliveryLabel(recipient: ReferralRecipient): string {
  if (recipient.channel === 'metriport') {
    return REFERRAL_CHANNEL_LABELS.metriport;
  }
  const faxNumber = recipient.destination.practice_location.fax_number;
  return `Fax ${formatPhoneNumber(faxNumber) || faxNumber}`;
}

/** ZIP and state to prefill an NPPES search, read from a free-text address. */
export function postalSearchDefaults(address: string | null | undefined): {
  postalCode: string;
  state: string;
} {
  const match = /\b([A-Z]{2})\s+(\d{5})(?:-\d{4})?\b/.exec(address ?? '');
  if (match) {
    return { state: match[1] ?? '', postalCode: match[2] ?? '' };
  }
  const zip = /\b(\d{5})(?:-\d{4})?\b(?!.*\b\d{5}\b)/.exec(address ?? '');
  return { state: '', postalCode: zip?.[1] ?? '' };
}
